import { FormErrors } from "./form-errors";
import { PasswordInput } from "./password-input";
import { PencilRuler } from "lucide-react";
import { Form, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  signUpPath,
  loginPath,
  oauthGooglePath,
  invitationAcceptancePath,
} from "@/routes";
import InputError from "@/components/ui/input-error";
import { Spinner } from "@/components/ui/spinner";

type InvitationProps = {
  code: string;
  email_address: string;
  account?: { name: string };
};

export function SignupForm({
  className,
  invitation,
  ...props
}: React.ComponentProps<"div"> & { invitation?: InvitationProps }) {
  const { t } = useTranslation();
  const { app_name: appName } = usePage().props;
  const handleGoogleSignup = () => {
    window.location.href = oauthGooglePath({
      invitation_code: invitation?.code,
    });
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Form
        action={signUpPath()}
        method="post"
        resetOnSuccess={["user[password]", "user[password_confirmation]"]}
        disableWhileProcessing
      >
        {({ processing, errors }) => (
          <FieldGroup>
            <FormErrors
              errors={errors}
              targets={{
                "user.email_address": "user_email_address",
                "user.password": "user_password",
                "user.password_confirmation": "user_password_confirmation",
              }}
            />
            <div className="flex flex-col items-center gap-2 text-center">
              <Link
                href="/login"
                className="flex flex-col items-center gap-2 font-medium"
              >
                <div className="flex size-8 items-center justify-center rounded-md">
                  <PencilRuler className="size-6" />
                </div>
                <span className="sr-only">{appName}</span>
              </Link>
              <h1 className="text-page font-display">
                {t("auth.signup")} · {appName}
              </h1>
              <FieldDescription>
                {t("auth.has_account")}{" "}
                <Link
                  href={
                    invitation
                      ? invitationAcceptancePath({ code: invitation.code })
                      : loginPath()
                  }
                >
                  {t("auth.login")}
                </Link>
              </FieldDescription>
              {invitation?.account && (
                <p className="text-sm text-muted-foreground">
                  {t("auth.joining", { account: invitation.account.name })}
                </p>
              )}
            </div>
            {invitation?.code && (
              <input
                type="hidden"
                name="invitation_code"
                value={invitation.code}
              />
            )}
            <Field>
              <FieldLabel htmlFor="user_email_address">
                {t("auth.email")}
              </FieldLabel>
              <Input
                id="user_email_address"
                name="user[email_address]"
                type="email"
                autoComplete="email"
                placeholder="m@example.com"
                disabled={processing}
                required
                autoFocus
                defaultValue={invitation?.email_address}
              />
            </Field>
            <InputError message={errors["user.email_address"]} />
            <Field>
              <FieldLabel htmlFor="user_password">
                {t("auth.password")}
              </FieldLabel>
              <PasswordInput
                id="user_password"
                name="user[password]"

                autoComplete="new-password"
                placeholder="********"
                required
              />
            </Field>
            <InputError message={errors["user.password"]} />
            <Field>
              <FieldLabel htmlFor="user_password_confirmation">
                {t("auth.password_confirmation")}
              </FieldLabel>
              <PasswordInput
                id="user_password_confirmation"
                name="user[password_confirmation]"

                autoComplete="new-password"
                placeholder="********"
                required
              />
            </Field>
            <InputError message={errors["user.password_confirmation"]} />
            <Field>
              <Button type="submit">
                {processing && <Spinner />}
                {t("auth.signup")}
              </Button>
            </Field>
            <InputError message={errors["user.base"]} />
            <FieldSeparator>{t("auth.or")}</FieldSeparator>
            <Field className="grid gap-4 sm:grid-cols-1">
              <Button
                variant="outline"
                type="button"
                onClick={handleGoogleSignup}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path
                    d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                    fill="currentColor"
                  />
                </svg>
                {t("auth.continue_google")}
              </Button>
            </Field>
          </FieldGroup>
        )}
      </Form>
    </div>
  );
}
